import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NUTRITION_ADD_REMOVE_PRODUCTS_ENABLED } from 'constants/appConstants';
import TaskRowContextMenu from 'pages/FarmsPage/Workspace/Tasks/TaskRowContextMenu';
import type { NutritionProductLine } from './computeNutritionAudit';
import type { NutritionProgramEdit } from './useNutritionProgramEdit';

/** Does nothing — the grid menu's actions this row doesn't offer. */
const noop = () => undefined;

/**
 * Right-click on a product row in Plan mode (OP-2523) opens the Tasks grid's
 * own row menu (TaskRowContextMenu) with Delete, which removes only that
 * product; an application left with nothing on it is removed too, and Undo
 * puts it back. As in the grid, the row turns the selected green
 * (.highlighted-row) while its menu is open, and the menu closes on any
 * click or right-click outside it, or Escape. Locked rows, Actual mode and a
 * row whose save is in flight get no menu — the rate box says why.
 *
 * The menu is portalled to the body: it's positioned `fixed`, and a
 * transformed ancestor would otherwise become its containing block.
 */
const ProductRowMenu: React.FC<{
  line: NutritionProductLine;
  edit: NutritionProgramEdit;
  /** The product's table row. */
  children: React.ReactElement<{
    className?: string;
    onContextMenu?: React.MouseEventHandler;
  }>;
}> = ({ line, edit, children }) => {
  const [position, setPosition] = useState<{ x: number; y: number } | null>(
    null
  );
  const menuRef = useRef<HTMLDivElement | null>(null);
  const close = useCallback(() => setPosition(null), []);

  // Same rule as the grid's useStepContextMenu: any press outside the menu
  // closes it (a right-click on another row then opens that row's menu).
  useEffect(() => {
    if (!position) return undefined;
    const onOutside = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) close();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onOutside);
    document.addEventListener('contextmenu', onOutside);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('mousedown', onOutside);
      document.removeEventListener('contextmenu', onOutside);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', close, true);
    };
  }, [close, position]);

  if (
    !NUTRITION_ADD_REMOVE_PRODUCTS_ENABLED ||
    !edit.canEdit ||
    edit.lockReason(line) ||
    edit.isSaving(line)
  ) {
    return children;
  }

  const row = React.cloneElement(children, {
    className: [children.props.className, position && 'highlighted-row']
      .filter(Boolean)
      .join(' '),
    onContextMenu: (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setPosition({ x: event.clientX, y: event.clientY });
    },
  });

  return (
    <>
      {row}
      {position &&
        createPortal(
          <TaskRowContextMenu
            ref={menuRef}
            x={position.x}
            y={position.y}
            alignRight={false}
            showViewDetails={false}
            showReorder={false}
            onViewDetails={noop}
            onReorder={noop}
            onDelete={() => {
              close();
              edit.removeProduct(line);
            }}
          />,
          document.body
        )}
    </>
  );
};

export default ProductRowMenu;
